import { CalendarIcon } from '@diligentcorp/atlas-react-icons';
import {
  useDatePickerKeyboard,
  useDateTimeFormat,
  useDateTimePlaceholder,
} from '@issuemanager/apps/client/hooks';
import { adjustDateForClientDisplay } from '@issuemanager/apps/client/utils';
import { DateTimePicker, type DateTimePickerProps } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useMemo, useRef, useState, type JSX, type MouseEvent } from 'react';
import {
  Controller,
  type FieldValues,
  type UseControllerProps,
} from 'react-hook-form';
import { TextField, type TextFieldProps } from '../text-field';

interface ControlledTextFieldProps<
  TFieldValues extends FieldValues,
  TInputDate,
  TDate,
> extends Pick<
      TextFieldProps,
      | 'label'
      | 'minRows'
      | 'multiline'
      | 'labelInfo'
      | 'disabled'
      | 'data-testid'
      | 'inputProps'
    >,
    UseControllerProps<TFieldValues>,
    Omit<
      DateTimePickerProps<TInputDate, TDate>,
      'renderInput' | 'value' | 'onChange'
    > {
  onChange?: (value: TDate | null) => void;
}

export function ControlledDateField<
  TFieldValues extends FieldValues,
  TInputDate,
  TDate,
>({
  name,
  control,
  rules,
  label,
  labelInfo,
  disablePast,
  disableFuture,
  maxDate,
  maxDateTime,
  onChange,
  ...props
}: ControlledTextFieldProps<TFieldValues, TInputDate, TDate>): JSX.Element {
  const { date: dateFormat, timezoneOffset } = useDateTimeFormat();

  const inputFormat = `${dateFormat} hh:mm A`;
  const localizedPlaceholder = useDateTimePlaceholder(inputFormat);

  const openPickerButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useDatePickerKeyboard({
    isPickerOpen,
    onClose: () => setIsPickerOpen(false),
  });

  const handleInputClick = (
    e: MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    e.stopPropagation();
    openPickerButtonRef.current?.click();
  };

  const transformedMaxDateTime = useMemo(() => {
    if (!maxDateTime) return undefined;
    // Use adjustDateForClientDisplay to convert UTC date to session timezone
    // and adjust for browser timezone to "fool" the DateTimePicker
    const utcDateString = dayjs(maxDateTime as unknown as Date)
      .utc()
      .toISOString();
    return adjustDateForClientDisplay(
      utcDateString,
      timezoneOffset,
      0,
    ) as TDate;
  }, [maxDateTime, timezoneOffset]);

  return (
    <Controller<TFieldValues>
      control={control}
      rules={rules}
      name={name}
      render={({
        field: { onChange: handleChange, value },
        fieldState: { error },
      }) => {
        const testId = props['data-testid'] ?? name;

        const handleDateTimeChange = (newValue: unknown): void => {
          if (newValue && dayjs(newValue as Date).isValid()) {
            // Revert the timezone adjustment to get the actual selected time
            const localTimezoneOffset = new Date().getTimezoneOffset();
            const revertedDate = dayjs(newValue as Date)
              .subtract(localTimezoneOffset, 'minute')
              .subtract(timezoneOffset, 'minute');

            const adjusted = revertedDate.utc();
            handleChange(adjusted);
            onChange?.(adjusted as TDate);
          } else {
            handleChange(null);
            onChange?.(null as TDate);
          }
        };

        const handlePickerOpen = (): void => {
          setIsPickerOpen(true);
          openPickerButtonRef.current?.focus();
        };

        const handlePickerClose = (): void => {
          setIsPickerOpen(false);
        };

        return (
          <DateTimePicker
            disabled={props.disabled}
            disablePast={disablePast}
            disableFuture={disableFuture}
            readOnly={props.readOnly}
            maxDate={maxDate}
            maxDateTime={transformedMaxDateTime}
            inputFormat={inputFormat}
            open={isPickerOpen}
            value={
              value
                ? adjustDateForClientDisplay(
                    dayjs.utc(value).toISOString(),
                    timezoneOffset,
                    0,
                  )
                : null
            }
            onChange={handleDateTimeChange}
            onOpen={handlePickerOpen}
            onClose={handlePickerClose}
            OpenPickerButtonProps={{
              ref: (ref) => {
                openPickerButtonRef.current = ref;
              },
            }}
            components={{ OpenPickerIcon: CalendarIcon }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                labelInfo={labelInfo && <span>{labelInfo}</span>}
                aria-labelledby={`${name}-label`}
                data-testid={testId}
                required={
                  props.InputProps?.required || Boolean(rules?.required)
                }
                error={Boolean(error?.message)}
                helperText={error?.message}
                inputProps={{
                  ...params.inputProps,
                  tabIndex: -1,
                  'data-testid': `${testId}-input`,
                  onClick: handleInputClick,
                  placeholder: localizedPlaceholder,
                  onKeyDown: (e) => {
                    e.preventDefault();
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
